import { type MouseEvent, useState } from 'react';
import type { ISnapshot } from './SnapshotTypes';

export interface UseMultiSelectionOptions {
  snapshots: ISnapshot[];
  selectedSnapshotIds?: string[] | null;
  onAddSelectedItems: (snapshotIds: string[]) => void;
}

export interface UseMultiSelectionReturn {
  handleSelectSnapshot: (snapshotId: string, keyPressed: MouseEvent) => void;
}

export const useMultiSelection = ({
  snapshots,
  selectedSnapshotIds,
  onAddSelectedItems,
}: UseMultiSelectionOptions): UseMultiSelectionReturn => {
  const [lastSelectedSnapshotId, setLastSelectedSnapshotId] = useState<
    string | null
  >(null);
  const [lastSelectedSliceWithShiftKey, setLastSelectedSliceWithShiftKey] =
    useState<string[]>([]);

  const getSnapshotById = (snapshotId: string): ISnapshot | undefined => {
    return snapshots.find((snapshot) => snapshot.id === snapshotId);
  };

  /**
   * Finds the closest snapshot index to the given targetIndex from a set of indexCandidates.
   * Returns the snapshot id with the closest index or null if no snapshots are provided.
   */
  const findClosestIndex = (
    targetIndex: number,
    indexCandidates: { id: string; index: number }[],
  ): { id: string; index: number } | null => {
    if (indexCandidates.length === 0) {
      return null;
    }

    // Fixed noUncheckedIndexedAccess NIM-19408
    let closest = indexCandidates[0]!;
    let minDistance = Math.abs(targetIndex - closest.index);

    for (let i = 1; i < indexCandidates.length; i++) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const distance = Math.abs(targetIndex - indexCandidates[i]!.index);
      if (distance < minDistance) {
        minDistance = distance;
        // Fixed noUncheckedIndexedAccess NIM-19408
        closest = indexCandidates[i]!;
      }
    }

    return closest;
  };

  /**
   * Handles the situation when there is no last selected snapshot by click and in once multiple snapshots are
   * selected (e.g., from the URL).
   * Finds the snapshot closest to the clicked one from the selected snapshots, or returns the last selected one if it
   * exists.
   */
  const getClosestLastSelectedSnapshotId = (idSnapshot: string): string => {
    if (lastSelectedSnapshotId) {
      return lastSelectedSnapshotId;
    }

    // Build a map of selected snapshot IDs to their indexes in the snapshots array
    const selectedIndexes: Record<string, number> = {};
    let indexOfClickedSnapshot = -1;

    snapshots.forEach((snapshot, index) => {
      if (snapshot.id === idSnapshot) {
        indexOfClickedSnapshot = index;
      }
      if (selectedSnapshotIds?.includes(snapshot.id ?? '')) {
        selectedIndexes[snapshot.id ?? ''] = index;
      }
    });

    if (indexOfClickedSnapshot === -1) {
      return '';
    }

    // Prepare candidates for closest selection
    const indexCandidates = Object.entries(selectedIndexes).map(
      ([id, index]) => ({
        id,
        index,
      }),
    );

    return findClosestIndex(indexOfClickedSnapshot, indexCandidates)?.id || '';
  };

  /**
   * Returns a slice of snapshots between the last selected snapshot and the currently clicked snapshot.
   * If the last selected snapshot is the same as the currently clicked snapshot, it returns only that snapshot.
   * If no last selected snapshot exists, it returns an empty array.
   */
  const getSliceBetweenSelectedAndLastClicked = (
    snapshotId: string,
  ): ISnapshot[] => {
    const lastSelected = getClosestLastSelectedSnapshotId(snapshotId);
    if (lastSelected === snapshotId) {
      const snapshot = getSnapshotById(snapshotId);
      return snapshot ? [snapshot] : [];
    }
    const indexOfLastSelected = snapshots.findIndex(
      (snapshot) => snapshot.id === lastSelected,
    );
    for (const [index, snapshot] of snapshots.entries()) {
      if (snapshot.id === snapshotId) {
        if (index < indexOfLastSelected) {
          return snapshots.slice(index, indexOfLastSelected + 1);
        }
        return snapshots.slice(indexOfLastSelected, index + 1);
      }
    }
    return []; // Should not happen, but just in case
  };

  // Helper for shift key selection
  const handleShiftSelection = (snapshotId: string) => {
    const newSelectionSlice = getSliceBetweenSelectedAndLastClicked(snapshotId);
    const newSelectionSliceIds = newSelectionSlice.map((s) => s.id ?? '');
    if (lastSelectedSliceWithShiftKey.length) {
      const previouslySelected = selectedSnapshotIds ?? [];
      const filteredSelection = previouslySelected.filter(
        (id) => !lastSelectedSliceWithShiftKey.includes(id),
      );
      const merged = [
        ...new Set([...filteredSelection, ...newSelectionSliceIds]),
      ];
      onAddSelectedItems(merged);
    } else {
      const merged = [
        ...new Set([...(selectedSnapshotIds ?? []), ...newSelectionSliceIds]),
      ];
      onAddSelectedItems(merged);
    }
    setLastSelectedSliceWithShiftKey(newSelectionSliceIds);
  };

  // Helper for ctrl/cmd key selection
  const handleCtrlCmdSelection = (snapshotId: string) => {
    setLastSelectedSliceWithShiftKey([]);
    if (selectedSnapshotIds?.includes(snapshotId)) {
      if ((selectedSnapshotIds?.length ?? 0) !== 1) {
        onAddSelectedItems(
          selectedSnapshotIds.filter((id) => id !== snapshotId),
        );
      }
    } else {
      onAddSelectedItems([...(selectedSnapshotIds ?? []), snapshotId]);
    }
  };

  // Helper for single click selection
  const handleSingleSelection = (snapshotId: string) => {
    setLastSelectedSnapshotId(snapshotId);
    onAddSelectedItems([snapshotId]);
  };

  const handleSelectSnapshot = (snapshotId: string, event: MouseEvent) => {
    const shiftKey = event.shiftKey;
    const ctrlCmdKey = event.ctrlKey || event.metaKey;
    if (shiftKey || ctrlCmdKey) {
      if (shiftKey) {
        event.preventDefault();
        handleShiftSelection(snapshotId);
      } else {
        setLastSelectedSliceWithShiftKey([]); // Reset shift selection state
      }
      if (ctrlCmdKey) {
        handleCtrlCmdSelection(snapshotId);
      }
      setLastSelectedSnapshotId((prev) => {
        if (shiftKey) {
          return prev; // ignore shift key for last selected snapshot
        }
        if (ctrlCmdKey && prev === snapshotId) {
          return null; // Reset last selected if Ctrl/Cmd clicked on the same snapshot
        }
        return snapshotId;
      });
      return;
    }
    handleSingleSelection(snapshotId);
  };

  return {
    handleSelectSnapshot,
  };
};
